import intentHolder, {INTENT_FOR_EXISTING_CUSTOMER, INTENT_FOR_NEW_CUSTOMER} from "@/models/intent-holder";
import { deepGet } from "@/utils/helper-functions"
import PaymentHelper from "@/utils/payment-helper";
import { Gateway, UpperCaseGateway } from '@/models/gateway-type';
import {paymentMethods} from "@/utils/payment-helper";
import {PostPaymentActionsSubmitter} from "@/utils/post-payment-actions-submitter";
import { PaymentErrorCode } from '@/utils/error-codes/payment';
import logger from '@/utils/global-logger';

let btClientInstance = undefined;
export function updateBTClientInstance(instance) {
    btClientInstance = instance;
}

export const DEFAULT_PI_AMOUNT = 100

export function getCardBillingAddress(pm) {
  return {
    firstName: pm.first_name,
    lastName: pm.last_name,
    addressLine1: pm.billing_addr1,
    addressLine2: pm.billing_addr2,
    city: pm.billing_city,
    state: pm.billing_state,
    stateCode: pm.billing_state_code,
    countryCode: pm.billing_country,
    zip: pm.billing_zip,
  }
}

function getCustomerInfo(store) {
  const customer = store.state.customer.data || {};
  return {
    firstName: customer.first_name,
    lastName: customer.last_name,
    phone: customer.phone,
    email: customer.email,
  }
}

function getCustomerHandle() {
  return window && window.hpData && window.hpData.content && window.hpData.content.customer_handle || undefined;
}

export function getPaymentIntentAmount(pmType, getters) {
  let amount = getters.hosted_page_type === 'paynow' ? getters.amountToBePaid : getters.current_amount_due;
  let pm = getters.pm_by_type(pmType);
  // if zero then set it from token amount
  if(!amount && (pm && pm.token_amount) != undefined) {
    amount = pm.token_amount;
  }
  if(amount == undefined) {
    amount = DEFAULT_PI_AMOUNT;
  }
  return amount;
}

export function createPaymentIntent(store, payment_method = {}, options = {}) {
  const getters = store.getters;
  const state = store.state;
  const params = {
    amount: undefined,
    currencyCode: getters.currency_code || store.state.customer.data.preferred_currency_code,
    intent: getters.payment_intent,
    paymentMethodType: payment_method.type
  }

  if(options.paymentMethodType) {
    params.paymentMethodType = options.paymentMethodType
  }
  if(options.postAction) {
    params.postActionJson = options.postAction
  }
  if(options.amount != undefined) {
    params.amount = options.amount
  } else {
    params.amount = getPaymentIntentAmount(params.paymentMethodType, getters)
  }

  if(state.customer?.temp_payment_intent) params.tempPaymentIntent = state.customer.temp_payment_intent;
  return Promise.resolve().then(() => {
    if(state.auth.authenticated) {
      params["customerId"] = getters.customer_handle
      params["paymentSourceId"] = payment_method.reference_id
      params["gatewayId"] = payment_method.gateway_account_id
      return intentHolder.set(INTENT_FOR_EXISTING_CUSTOMER, params);
      }
    else {
      params["gatewayId"] = payment_method.gateway_account_id
      return intentHolder.set(INTENT_FOR_NEW_CUSTOMER, params);
    }
  }).then(({payment_intent}) => {
    // For apple pay/google pay we are creating a new payment intent when button is loaded, so If user authorizes a payment intent then
    // we moved user to summary page but if user clicks in payment edit button we will load apple pay/google pay button again and will create a new payment
    // intent and if he will click back without authorizing new payment intent we will get error because new payment intent is not authorized
    // so we will use skipSetPaymentIntent to not set payment intent on button load.
    if(options.skipSetPaymentIntent) {
      // we will skip but store this in temp variable
      store.dispatch("set_temp_payment_intent", payment_intent);
    } else {
      store.dispatch("set_payment_intent", payment_intent);
      store.dispatch("set_temp_payment_intent", undefined);
    }

    if(window.cbStorage && window.cbStorage.localStorageEnabled) {
      window.cbStorage.setItem("cb_payment_intent_id", payment_intent.id)
    }

    // Log payment intent creation
    if (payment_intent && getters && state) {
      const pm = getters.pm_by_type ? getters.pm_by_type(payment_method?.type) : null;
      const gateway = payment_intent?.gateway || payment_method?.gateway || (pm?.gateway_name) || null;
      store.dispatch('add_debug_log', {
        type: 'KVL',
        data: {
          hp_log_type: 'payment_intent_debug',
          payment_intent_created: Date.now(),
          intent_amount: params?.amount || null,
          current_amount_due: getters?.current_amount_due || null,
          gateway: gateway,
          hp_token: state?.auth?.hpToken || null,
        },
      });
    }

    return payment_intent;
  })
}

export function threeDSHandlerWithMandate(store, payment_method, options = {}, mandateDescription) {
  const pm = store.getters.pm_by_type(payment_method.type);
  if(PaymentHelper.shouldSendMandateInfoToGateway(pm)) {
    return threeDSHandler(store, payment_method, options, { requireMandate: true, description: mandateDescription })
  }
  return threeDSHandler(store, payment_method, options)
}

export function threeDSHandler(store, payment_method, options = {}, mandate) {
  PaymentHelper.setConfig(store.state, store.getters);
  const getters = store.getters;
  let threeDShandler;
  return window.cbjsLoader.then(() => {
    threeDShandler = Chargebee.getInstance().create3DSHandler();
    const pm = store.getters.pm_by_type(payment_method.type);
    const gateway = payment_method?.gateway || pm?.gateway_name;
    // https://mychargebee.atlassian.net/browse/CHKOUTENGG-28750
    // To fix popup blocker
    if([UpperCaseGateway.MOLLIE, UpperCaseGateway.TWIKEY].includes(gateway.toUpperCase())) {
      threeDShandler.openNewWindow();
    }
  }).catch((err) => {
    logger.error(PaymentErrorCode.CHARGEBEE_JS_COMPONENT_LOAD_FAILED, { phase: 'create_3ds_handler' }, err);
    throw err;
  })
  .then(() => createPaymentIntent(store, payment_method, options))
  .then(payment_intent => {
      let options = {}, additionalInfo ={};
      if(payment_intent.gateway == Gateway.BRAINTREE && btClientInstance) {
          options[Gateway.BRAINTREE] = btClientInstance;
      }
      threeDShandler.setPaymentIntent(payment_intent, options);
      let params3ds = {}
      if(payment_method.tmp_token) {
          params3ds['tokenizer'] = () => Promise.resolve(payment_method.tmp_token);
      }
      else if(payment_method.number) {
        params3ds['card'] = {
          firstName:  payment_method.first_name,
          lastName: payment_method.last_name,
          number: payment_method.number.replace(/\s+/g, ''),
          cvv: payment_method.cvv,
          expiryMonth: payment_method.expiry_month,
          expiryYear: payment_method.expiry_year > 100 ? payment_method.expiry_year : (2000 + parseInt(payment_method.expiry_year))
        }
        if(payment_intent.gateway == Gateway.STRIPE || payment_intent.gateway == Gateway.ADYEN) {
          params3ds['card']['preferredScheme'] = payment_method.preferred_scheme
        }
        additionalInfo['plan'] = deepGet(getters.cart_plan, 'details.invoice_name') || deepGet(getters.cart_plan, 'details.name') || deepGet(getters.cart_plan, 'id');
        additionalInfo['shippingAddress'] = PaymentHelper.getShippingAddress(store);
        additionalInfo['email'] = getters.customer_data.email;
        additionalInfo['billingAddress'] = getCardBillingAddress(payment_method);
        additionalInfo['cardBillingAddress'] = getCardBillingAddress(payment_method);
        additionalInfo['customerBillingAddress'] = PaymentHelper.getCustomerBillingAddress(store);
        additionalInfo['customer'] = getCustomerInfo(store);
        additionalInfo['locale'] = getters.init_info_locale && getters.init_info_locale.slice(0,2);
      }
      additionalInfo['email'] = getters.customer_data.email || payment_method.email;
      additionalInfo['phone'] = getters.customer_data.phone || payment_method.phone;
      if([Gateway.EBANX, Gateway.DLOCAL].includes(payment_intent.gateway)) {
        additionalInfo['document'] = {
          number: payment_method.document_number,
          type : payment_method.document_type
        }
        additionalInfo['is3dsRequired'] = getters.threeDSNeeded;
        let paymentData = (payment_method.reference_id) ? payment_method.card : payment_method;
        additionalInfo['billingAddress'] = additionalInfo['billingAddress'] || getCardBillingAddress(paymentData);
        additionalInfo['cardBillingAddress'] = additionalInfo['cardBillingAddress'] || getCardBillingAddress(paymentData);
      }
      if(mandate && mandate.requireMandate) {
        additionalInfo['mandate'] = {
          requireMandate: mandate.requireMandate,
          description: mandate.description || deepGet(getters.cart_plan, 'details.invoice_name') || deepGet(getters.cart_plan, 'details.name') || deepGet(getters.cart_plan, 'id') || 'Subscription',
        };
      }
      if(payment_intent.gateway == Gateway.RAZORPAY) {
        additionalInfo['customer'].firstName = payment_method.first_name;
        additionalInfo['customer'].lastName = payment_method.last_name;
        additionalInfo['customer'].phone = payment_method.phone;
        additionalInfo['customer'].email = payment_method.email;
        // For paynow additionalInfo['mandate'] will be true if customer check the save payment
        Object.assign(additionalInfo, PaymentHelper.getRazorpayAdditionalData(payment_method.type, {shippingAddress: additionalInfo.shippingAddress, retain_card: !!additionalInfo['mandate']}));
      }

      if(getCustomerHandle()) {
        additionalInfo['metaData'] = {
          "cb_customer_id": getCustomerHandle()
        }
      }
      if(Object.keys(additionalInfo).length > 0) {
        params3ds['additionalData'] = additionalInfo;
      }
      return threeDShandler.handleCardPayment(params3ds).then((intent) => {
        store.dispatch("set_payment_intent", intent);
        
        // Log three DS completion
        if (intent && getters && store?.state?.auth) {
          store.dispatch('add_debug_log', {
            type: 'KVL',
            data: {
              hp_log_type: 'three_ds_debug',
              hp_token: store.state.auth.hpToken || null,
              three_ds_completed: Date.now(),
              authorized_amount: intent?.amount || null,
              current_amount_due: getters?.current_amount_due || null,
            },
          });
        }
        
        return intent;
      });
  })
}

export function threeDSHandlerWithMandatePostPaymentActions(store, payment_method, options = {}, mandateDescription, {
  hpType, hpOptions = {}, submitter
} = {}) {
  const pm = store.getters.pm_by_type(payment_method.type);
  if (PaymentHelper.shouldSendMandateInfoToGateway(pm)) {
    return threeDSHandlerPostPaymentActions(store, payment_method, options, {
      requireMandate: true, description: mandateDescription
    }, {hpType, hpOptions, submitter});
  }
  return threeDSHandlerPostPaymentActions(store, payment_method, options, undefined, {hpType, hpOptions, submitter});
}

export function threeDSHandlerPostPaymentActions(store, payment_method, options = {}, mandate, {
  hpType,
  hpOptions = {},
  submitter
} = {}) {
  PaymentHelper.setConfig(store.state, store.getters);
  const getters = store.getters;
  let threeDShandler;
  let response;
  return window.cbjsLoader
    .then(() => {
      threeDShandler = Chargebee.getInstance().create3DSHandler();
      const pm = store.getters.pm_by_type(payment_method.type);
      const gateway = payment_method?.gateway || pm?.gateway_name;
      // https://mychargebee.atlassian.net/browse/CHKOUTENGG-28750
      // To fix popup blocker
      if ([UpperCaseGateway.MOLLIE, UpperCaseGateway.TWIKEY].includes(gateway.toUpperCase())) {
        threeDShandler.openNewWindow();
      }
    })
    .catch((err) => {
      logger.error(PaymentErrorCode.CHARGEBEE_JS_COMPONENT_LOAD_FAILED, { phase: 'create_3ds_handler' }, err);
      throw err;
    })
    .then(() => createPaymentIntent(store, payment_method, options))
    .then(payment_intent => {
      let options = {}, additionalInfo = {};
      if (payment_intent.gateway == Gateway.BRAINTREE && btClientInstance) {
        options[Gateway.BRAINTREE] = btClientInstance;
      }
      threeDShandler.setPaymentIntent(payment_intent, options);
      let params3ds = {};
      if (payment_method.tmp_token) {
        params3ds["tokenizer"] = () => Promise.resolve(payment_method.tmp_token);
      } else if (payment_method.number) {
        params3ds["card"] = {
          firstName: payment_method.first_name,
          lastName: payment_method.last_name,
          number: payment_method.number.replace(/\s+/g, ""),
          cvv: payment_method.cvv,
          expiryMonth: payment_method.expiry_month,
          expiryYear: payment_method.expiry_year > 100 ? payment_method.expiry_year : 2000 + parseInt(payment_method.expiry_year)
        };
        if (payment_intent.gateway == Gateway.STRIPE || payment_intent.gateway == Gateway.ADYEN) {
          params3ds["card"]["preferredScheme"] = payment_method.preferred_scheme;
        }
        additionalInfo["plan"] = deepGet(getters.cart_plan, "details.invoice_name") || deepGet(getters.cart_plan, "details.name") || deepGet(getters.cart_plan, "id");
        additionalInfo["shippingAddress"] = PaymentHelper.getShippingAddress(store);
        additionalInfo["email"] = getters.customer_data.email;
        additionalInfo["billingAddress"] = getCardBillingAddress(payment_method);
        additionalInfo["cardBillingAddress"] = getCardBillingAddress(payment_method);
        additionalInfo["customerBillingAddress"] = PaymentHelper.getCustomerBillingAddress(store);
        additionalInfo["customer"] = getCustomerInfo(store);
        additionalInfo["locale"] = getters.init_info_locale && getters.init_info_locale.slice(0, 2);
      }
      additionalInfo["email"] = getters.customer_data.email || payment_method.email;
      additionalInfo["phone"] = getters.customer_data.phone || payment_method.phone;
      if ([Gateway.EBANX, Gateway.DLOCAL].includes(payment_intent.gateway)) {
        additionalInfo["document"] = {
          number: payment_method.document_number, type: payment_method.document_type
        };
        additionalInfo["is3dsRequired"] = getters.threeDSNeeded;
        let paymentData = payment_method.reference_id ? payment_method.card : payment_method;
        additionalInfo["billingAddress"] = additionalInfo["billingAddress"] || getCardBillingAddress(paymentData);
        additionalInfo["cardBillingAddress"] = additionalInfo["cardBillingAddress"] || getCardBillingAddress(paymentData);
      }
      if (mandate && mandate.requireMandate) {
        additionalInfo["mandate"] = {
          requireMandate: mandate.requireMandate,
          description: mandate.description || deepGet(getters.cart_plan, "details.invoice_name") || deepGet(getters.cart_plan, "details.name") || deepGet(getters.cart_plan, "id") || 'Subscription',
        };
      }
      if (payment_intent.gateway == Gateway.RAZORPAY) {
        additionalInfo["customer"].firstName = payment_method.first_name;
        additionalInfo["customer"].lastName = payment_method.last_name;
        additionalInfo["customer"].phone = payment_method.phone;
        additionalInfo["customer"].email = payment_method.email;
        // For paynow additionalInfo['mandate'] will be true if customer check the save payment
        Object.assign(additionalInfo, PaymentHelper.getRazorpayAdditionalData(payment_method.type, {
          shippingAddress: additionalInfo.shippingAddress, retain_card: !!additionalInfo["mandate"]
        }));
      }

      if (getCustomerHandle()) {
        additionalInfo["metaData"] = {
          cb_customer_id: getCustomerHandle()
        };
      }
      if (Object.keys(additionalInfo).length > 0) {
        params3ds["additionalData"] = additionalInfo;
      }

      PostPaymentActionsSubmitter.kvlPostPaymentActionsLogger(hpType, { intent : payment_intent}, "threeDSHandlerPostPaymentActions", store);
      const postPaymentActionsSubmitter = new PostPaymentActionsSubmitter(submitter.newCheckoutSubmitter, submitter.loadAndSubmitGift, submitter._submitPaynow, submitter.paymentHelper);

      return postPaymentActionsSubmitter.submit(hpType, payment_intent, hpOptions, payment_method).then(resp => {
        response = resp;
        return threeDShandler.handleCardPayment(params3ds);
      }).then(intent => {
        store.dispatch("set_payment_intent", intent);
        
        // Log three DS completion
        if (intent && getters && store?.state?.auth) {
          store.dispatch('add_debug_log', {
            type: 'KVL',
            data: {
              hp_log_type: 'three_ds_debug',
              hp_token: store.state.auth.hpToken || null,
              three_ds_completed: Date.now(),
              authorized_amount: intent?.amount || null,
              current_amount_due: getters?.current_amount_due || null,
            },
          });
        }
        
        return {
          intent,
          response,
          redirect_payment_method: paymentMethods.CARD
        };
      });
    });
}
